import { useEffect, useRef, useState } from 'preact/hooks';
import { PanelHeader } from './PanelHeader';
import { PreChatForm } from './PreChatForm';
import { InlineCaptureForm } from './InlineCaptureForm';
import { OfflineForm } from './OfflineForm';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { useComposerAttachment } from './useComposerAttachment';
import type { AttachmentsConfig } from '../client/api-types';
import type { UploadedAttachment } from '../client/attachments';
import { QuickReplies } from './QuickReplies';
import { useFocusTrap } from './useFocusTrap';
import { MirloLogoIcon, PaperclipIcon } from './icons';
import { cn } from '../lib/cn';
import { useTheme } from './useTheme';
import { LAUNCHER_SIZES, resolveLauncherSize } from './launcherSize';
import { tokens, darkenHex } from '../lib/theme';
import { formatNextAvailable } from '../lib/availability';
import { getLocale } from '../lib/i18n';
import type {
  BrandingConfig,
  PreChatFormConfig,
  AvailabilityConfig,
} from '../client/api-types';
import type {
  ActiveResponder,
  AvailabilityStatus,
  CaptureMode,
  ChatMessage,
  OfflineFormStatus,
  PreChatData,
} from './useChatState';
import type {
  ConnectionStatus,
  OfflineMessageErrorEvent,
} from '../client/socket';
import type { QuickReplyOption } from './QuickReplies';
import { t } from '../lib/i18n';

function PoweredByFooter() {
  return (
    <div class="flex items-center justify-center gap-1 pb-2 bg-w-composer transition-[background] duration-[350ms]">
      <a
        href="https://mirlo.com"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center gap-1 no-underline"
      >
        <span class="text-[11px] text-w-secondary/70">{t('powered_by')}</span>
        <MirloLogoIcon class="w-2.5 h-2.5" />
        <span class="text-[11px] font-semibold text-w-bb-fg">mirlo</span>
      </a>
    </div>
  );
}

interface ChatPanelProps {
  /** Visitor attachment policy. Absent = the composer offers no attach button. */
  attachments?: AttachmentsConfig;
  onUploadAttachment?: (file: File) => Promise<UploadedAttachment>;
  branding: BrandingConfig;
  preChatForm: PreChatFormConfig | null;
  captureMode?: CaptureMode;
  messages: ChatMessage[];
  preChatDone: boolean;
  connectionStatus: ConnectionStatus;
  inputDisabled: boolean;
  agentTyping: boolean;
  activeResponder?: ActiveResponder | null;
  availabilityStatus?: AvailabilityStatus;
  availability?: AvailabilityConfig;
  onClose: () => void;
  onPreChatSubmit: (data: PreChatData) => void;
  onSendMessage: (text: string, attachment?: UploadedAttachment) => void;
  onOfflineSubmit?: (data: Record<string, string>) => void;
  offlineFormStatus?: OfflineFormStatus;
  offlineFormError?: OfflineMessageErrorEvent | null;
  onOfflineRetry?: () => void;
  quickReplies?: {
    style: 'chips' | 'list';
    options: QuickReplyOption[];
  } | null;
  /**
   * True while conversational capture owns the input. Suppresses quick replies
   * so the visitor isn't offered shortcuts mid-questionnaire.
   */
  captureActive?: boolean;
  /** True when the field being asked is optional — shows the skip chip. */
  captureCanSkip?: boolean;
  onCaptureSkip?: () => void;
  verified?: boolean;
  headerStyle?: 'rich' | 'compact';
  bubbleStyle?: 'rounded' | 'classic';
  defaultTheme?: 'light' | 'dark';
  /** Ref to the floating button — focus is returned here on close. */
  triggerRef?: { current: HTMLElement | null };
  /** When true, render as full page (no close button, no floating layout) */
  standalone?: boolean;
  /**
   * Fill the parent container instead of the viewport. Used by the
   * dashboard preview iframe so the panel resizes with the iframe
   * (which is smaller than the visual viewport). Implies `standalone`
   * ergonomics (no close button, no keyboard-close hook).
   */
  preview?: boolean;
  /**
   * The panel opened on its own (auto_open flag) instead of a visitor
   * click. Suppresses the initial focus grab so the visitor's keyboard
   * focus stays on the host page. The Tab-trap still activates if they
   * choose to tab into the panel.
   */
  autoOpened?: boolean;
}

export function ChatPanel({
  attachments,
  onUploadAttachment,
  branding,
  preChatForm,
  captureMode = 'pre_chat',
  messages,
  preChatDone,
  connectionStatus,
  inputDisabled,
  agentTyping,
  activeResponder = null,
  availabilityStatus = 'online',
  availability,
  onClose,
  onPreChatSubmit,
  onSendMessage,
  onOfflineSubmit,
  offlineFormStatus = 'idle',
  offlineFormError = null,
  onOfflineRetry,
  quickReplies,
  captureActive = false,
  captureCanSkip = false,
  onCaptureSkip,
  verified = false,
  headerStyle = 'rich',
  bubbleStyle = 'rounded',
  defaultTheme = 'light',
  triggerRef,
  standalone = false,
  preview = false,
  autoOpened = false,
}: ChatPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  const closeCalled = useRef(false);
  const [quickRepliesVisible, setQuickRepliesVisible] = useState(true);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const { theme, toggle: toggleTheme } = useTheme(defaultTheme);

  const showPreChat = captureMode === 'pre_chat' && preChatForm && !preChatDone;
  const showInlineCapture =
    captureMode === 'inline' && preChatForm && !preChatDone;
  // In preview mode there's no real socket (demoMode is on), so the state
  // stays at `disconnected` forever. Suppress the connecting/offline
  // banners so the operator doesn't see a false "sin conexión" alert
  // over what is really just a static live preview.
  const isConnecting =
    !preview &&
    (connectionStatus === 'connecting' || connectionStatus === 'reconnecting');
  const isDisconnected = !preview && connectionStatus === 'disconnected';

  // Show quick replies only after greeting, before user sends first message.
  // Careful: `options.length && ...` returns `0` when the array is empty,
  // and JSX renders `0` as visible text. Coerce to boolean.
  const userHasMessaged = messages.some((m) => m.role === 'user');
  const showQuickReplies = Boolean(
    quickReplies?.options.length &&
    quickRepliesVisible &&
    !userHasMessaged &&
    !showPreChat &&
    !showInlineCapture &&
    !captureActive,
  );

  // Focus trap — active while the panel is mounted and not in closing animation.
  // Preview mode never traps focus: the panel is inside an iframe on the
  // dashboard, and stealing focus back into it every render would fight
  // the operator's form editing. Auto-opened panels keep the trap logic
  // active (in case the visitor tabs in) but skip the initial focus grab.
  useFocusTrap({
    containerRef: panelRef,
    active: !closing && !standalone && !preview,
    returnFocusTo: triggerRef?.current ?? null,
    skipInitialFocus: autoOpened,
  });

  function callOnClose() {
    if (closeCalled.current) return;
    closeCalled.current = true;
    onClose();
  }

  function handleClose() {
    // Show confirmation if the visitor has sent at least one message
    // (active conversation). Skipped in standalone/preview modes.
    const hasConversation = messages.some((m) => m.role === 'user');
    if (hasConversation && !standalone && !preview) {
      setShowCloseConfirm(true);
      return;
    }
    setClosing(true);
  }

  function confirmClose() {
    setShowCloseConfirm(false);
    setClosing(true);
  }

  function cancelClose() {
    setShowCloseConfirm(false);
  }

  function handleAnimationEnd(e: AnimationEvent) {
    if (closing && e.target === panelRef.current) {
      callOnClose();
    }
  }

  useEffect(() => {
    if (!closing) return;
    const timer = setTimeout(callOnClose, 180);
    return () => clearTimeout(timer);
  }, [closing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape key
  useEffect(() => {
    if (standalone || preview) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (showCloseConfirm) {
          cancelClose();
        } else if (!closing) {
          handleClose();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [closing, standalone, preview]);

  function handleQuickReplySelect(value: string) {
    setQuickRepliesVisible(false);
    onSendMessage(value);
  }

  // Build CSS variables for the current theme
  const themeTokens = tokens[theme];
  const brandDark = darkenHex(branding.color, 0.15);
  const cssVars: Record<string, string> = {
    '--brand': branding.color,
    '--brand-dark': brandDark,
    ...themeTokens,
  };

  // The whole panel is the drop target, not just the input row: dropping a
  // file on the transcript is the natural gesture. The state lives here so
  // the composer can render the chip while the panel owns the drag.
  const { pending, dragging, acceptFile, clearAttachment, dropHandlers } =
    useComposerAttachment({ attachments, onUpload: onUploadAttachment });

  return (
    <div
      ref={panelRef}
      {...dropHandlers}
      role="dialog"
      aria-label={`Chat con ${branding.name}`}
      aria-modal={!standalone}
      onAnimationEnd={handleAnimationEnd}
      style={cssVars}
      class={[
        'flex flex-col overflow-hidden',
        // Panel-level background fallback. Sub-regions (MessageList, MessageInput,
        // PoweredByFooter) paint their own bgs; this catches any transparent gap
        // (quick replies row, inline capture spacing) so it doesn't leak through
        // to the host document — which in standalone can be OS-dark even while
        // the widget theme is light.
        'bg-w-bg transition-[background] duration-[350ms]',
        'shadow-[0_8px_32px_rgba(0,0,0,0.22)]',
        // Each branch carries exactly one position utility: it anchors the
        // drop-hint overlay (`absolute inset-0`) to the panel.
        preview
          ? 'relative w-full h-full'
          : standalone
            ? 'relative w-screen h-[100dvh]'
            : [
                'fixed inset-0 w-screen h-[100dvh]',
                // Exactly ONE position utility per breakpoint. An unscoped
                // `relative` here (added for the drop-hint overlay's containing
                // block) silently beat the mobile `fixed` — `.relative` is
                // emitted after `.fixed` in Tailwind's output — dropping the
                // panel back into flow inside the bottom-right-anchored host,
                // offset ~24px left and ~72px above the viewport. Above the
                // breakpoint `relative` does the job the previous `static` did
                // (unpinning the panel) AND anchors the overlay; below it,
                // `fixed` is already a containing block.
                'xs:relative xs:inset-auto',
                'xs:w-[380px] xs:max-w-[calc(100vw-16px)]',
                // dvh, not vh: iOS Safari's `100vh` excludes the browser chrome,
                // which in landscape makes the panel taller than what's visible.
                'xs:h-[600px]',
                LAUNCHER_SIZES[resolveLauncherSize(branding.launcher_size)]
                  .panelMaxHeight,
                'xs:rounded-2xl xs:mb-3',
                closing ? 'animate-panel-close' : 'animate-panel-open',
              ].join(' '),
      ].join(' ')}
    >
      {dragging && (
        <div
          data-testid="drop-hint"
          class={cn(
            'absolute inset-0 z-30 flex items-center justify-center',
            // Solid on purpose. A translucent scrim let the transcript bleed
            // through and the hint got lost in the text. Note `bg-w-bg/95`
            // would emit nothing: `w-bg` maps to a raw `var(--w-bg)`, not to
            // space-separated RGB channels, so Tailwind cannot synthesize an
            // alpha variant from it.
            'bg-w-bg',
            'pointer-events-none',
          )}
        >
          <div
            class={cn(
              'flex flex-col items-center gap-2 px-6 py-5 rounded-[16px]',
              'border-2 border-dashed border-w-border bg-w-input',
              'text-[14px] text-w-bb-fg shadow-[0_4px_16px_rgba(0,0,0,0.12)]',
            )}
          >
            <PaperclipIcon class="w-6 h-6 opacity-60" />
            {t('drop_to_attach')}
          </div>
        </div>
      )}

      <PanelHeader
        branding={branding}
        onClose={standalone || preview ? () => {} : handleClose}
        hideClose={standalone || preview}
        connectionStatus={connectionStatus}
        agentTyping={agentTyping}
        activeResponder={activeResponder}
        verified={verified}
        headerStyle={headerStyle}
        theme={theme}
        onToggleTheme={toggleTheme}
        availabilityStatus={availabilityStatus}
        nextAvailableLabel={formatNextAvailable(
          availability?.next_available_at,
          getLocale(),
        )}
      />

      {/* Non-blocking status banner */}
      {isConnecting && (
        <div
          aria-live="polite"
          class="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-yellow-50 border-b border-yellow-100 text-yellow-700 text-xs"
        >
          <span class="inline-block w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
          {connectionStatus === 'reconnecting'
            ? t('reconnecting')
            : t('connecting')}
        </div>
      )}
      {isDisconnected && (
        <div
          aria-live="polite"
          class="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-50 border-b border-red-100 text-red-600 text-xs"
        >
          <span class="inline-block w-1.5 h-1.5 rounded-full bg-red-400" />
          {t('no_connection')}
        </div>
      )}

      {/*
        Offline-message banner: shown when the business is unavailable AND
        `offline_behavior === 'message'`. The chat stays open — visitor can
        still type — but a persistent banner sets expectations. The other
        two offline behaviours are handled separately: `form` renders
        `<OfflineForm>` below (replacing the composer), `hidden` bails out
        of the whole widget at the `Widget` level (floating button gone).

        This is the piece that was declared in the `OfflineBehavior` type
        union but never wired to UI. Before, `offline_behavior: 'message'`
        looked identical to online — silent bug.
      */}
      {availabilityStatus === 'offline' &&
        availability?.offline_behavior === 'message' && (
          <div
            aria-live="polite"
            class="flex flex-col items-center gap-0.5 px-3 py-2 bg-w-composer border-b border-w-border"
          >
            <div class="flex items-center gap-1.5 text-[13px] text-w-fg text-center">
              <span class="inline-block w-1.5 h-1.5 rounded-full bg-w-secondary/70" />
              <span>
                {availability?.offline_message || t('offline_default_message')}
              </span>
            </div>
            {(() => {
              const nextLabel = formatNextAvailable(
                availability?.next_available_at,
                getLocale(),
              );
              return nextLabel ? (
                <span class="text-[11px] text-w-secondary">{nextLabel}</span>
              ) : null;
            })()}
          </div>
        )}

      {/* Offline form: shown when unavailable and offline_behavior is 'form' */}
      {availabilityStatus === 'offline' &&
      availability?.offline_behavior !== 'message' ? (
        <OfflineForm
          brandingColor={branding.color}
          brandingName={branding.name}
          brandingLogo={branding.logo_url}
          offlineTitle={availability?.offline_title}
          offlineMessage={availability?.offline_message}
          nextAvailableLabel={formatNextAvailable(
            availability?.next_available_at,
            getLocale(),
          )}
          fields={(() => {
            const offlineFields = availability?.offline_form_fields;
            // If the visitor already completed the pre-chat form, filter out
            // fields that were already collected to avoid asking twice.
            if (
              preChatDone &&
              preChatForm?.fields?.length &&
              offlineFields?.length
            ) {
              const collectedNames = new Set(
                preChatForm.fields.map((f) => f.name),
              );
              const remaining = offlineFields.filter(
                (f) => !collectedNames.has(f.name),
              );
              return remaining.length > 0 ? remaining : undefined;
            }
            return offlineFields;
          })()}
          onSubmit={onOfflineSubmit ?? (() => {})}
          status={offlineFormStatus}
          error={offlineFormError}
          onRetry={onOfflineRetry}
        />
      ) : showPreChat ? (
        <PreChatForm
          config={preChatForm!}
          brandingColor={branding.color}
          brandingName={branding.name}
          brandingLogo={branding.logo_url}
          onSubmit={onPreChatSubmit}
        />
      ) : (
        <>
          <MessageList
            messages={messages}
            branding={branding}
            agentTyping={agentTyping}
            bubbleStyle={bubbleStyle}
            onReplyButtonSelect={onSendMessage}
          >
            {/* Inline capture: rendered inside the message list after the greeting */}
            {showInlineCapture && (
              <InlineCaptureForm
                config={preChatForm!}
                brandingColor={branding.color}
                onSubmit={onPreChatSubmit}
              />
            )}
          </MessageList>
          {showQuickReplies && (
            <QuickReplies
              options={quickReplies!.options}
              style={quickReplies!.style}
              brandingColor={branding.color}
              onSelect={handleQuickReplySelect}
            />
          )}
          {/* Conversational capture: let the visitor decline an optional field.
              Reuses the quick-reply chip row so it looks native. */}
          {captureCanSkip && onCaptureSkip && (
            <QuickReplies
              options={[{ label: t('capture_skip'), value: t('capture_skip') }]}
              style="chips"
              brandingColor={branding.color}
              onSelect={onCaptureSkip}
            />
          )}
          {inputDisabled ? (
            <div class="flex items-center justify-center px-4 py-3 bg-w-composer border-t border-w-border text-xs text-w-secondary italic">
              {t('chat_unavailable')}
            </div>
          ) : (
            <MessageInput
              onSend={onSendMessage}
              attachments={attachments}
              pending={pending}
              onPickFile={acceptFile}
              onClearAttachment={clearAttachment}
              brandingColor={branding.color}
              placeholder={branding.input_placeholder}
            />
          )}
          <PoweredByFooter />
        </>
      )}

      {/* Close confirmation overlay */}
      {showCloseConfirm && (
        <div class="absolute inset-0 z-10 flex items-center justify-center bg-black/30 animate-msg-in">
          <div class="bg-w-bb-bg rounded-2xl shadow-[0_10px_34px_rgba(0,0,0,0.18)] p-5 mx-4 max-w-[280px] text-center">
            <p class="m-0 text-[15px] font-semibold text-w-bb-fg mb-1">
              {t('close_confirm_title')}
            </p>
            <p class="m-0 text-[13px] text-w-secondary mb-4">
              {t('close_confirm_body')}
            </p>
            <div class="flex gap-2">
              <button
                type="button"
                onClick={cancelClose}
                class="flex-1 px-3 py-2 rounded-xl text-[14px] font-medium text-w-bb-fg bg-w-input border border-w-border cursor-pointer hover:bg-w-bg transition-colors"
              >
                {t('close_confirm_cancel')}
              </button>
              <button
                type="button"
                onClick={confirmClose}
                class="flex-1 px-3 py-2 rounded-xl text-[14px] font-medium text-white cursor-pointer hover:opacity-90 transition-opacity border-0"
                style={{ background: branding.color }}
              >
                {t('close_confirm_ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
